import React, { useContext, useEffect, useRef, useState } from 'react';
import { parse } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Flyout, FlyoutContent, FlyoutFooter, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { CharactersRemaining } from 'components/Form';
import { Button, ButtonGroup, Dropdown, Form, Input, Textarea } from 'components/HookForm';
import { openModal, closeModal } from 'components/Modal';
import Message from 'components/Message';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { itemPropType } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { reportError } from 'helpers/error-logging';
import { format12HourTimeLowercase } from 'helpers/intl-fns';
import { getDecagonChatPath, makePath } from 'helpers/link-generation';
import { selectActiveVenue } from 'selectors/general';
import { selectLocale } from 'selectors/locale-data';
import { selectModals } from 'components/Modal/Modal.selectors';

import { selectIsMobile } from 'selectors/ui';

import { updateItem } from '../Items.helpers';
import {
  DESCRIPTION_INPUT_HEIGHT_IN_LINES,
  DESCRIPTION_MAX_INPUT_LENGTH,
  EDIT_ITEM_FLYOUT_ID_PREFIX,
  NAME_MAX_INPUT_LENGTH,
} from '../Items.constants';
import { generateFlyoutId } from '../Items.utils';
import { ITEM_AVAILABILITY_MODAL_NAME } from './ItemAvailabilityModal';
import { ItemAvailabilityStatusDropdown } from './ItemAvailabilityStatus';
import { messages } from './ItemForm.messages';
import { ItemFormContext } from './ItemFormContext';

import './ItemForm.scss';

const { block, element } = bem('item-form');
const FORM_NAME = 'edit-item-form';
const MODAL_ID = 'item-availability-modal-edit';

const updateAvailabilityRecurrence = (availabilityRecurrence, updatedAvailability) => {
  return availabilityRecurrence.map(day => {
    const updatedAvailabilityDay = updatedAvailability.find(updatedDay => updatedDay.weekday === day.weekday);
    if (updatedAvailabilityDay) {
      return updatedAvailabilityDay;
    }
    return {
      ...day,
      available: false,
    };
  });
};

const EditItemForm = ({ isLoading, categoryTags, existingItemNames, foodAndBeverageL1Tag, items, onSuccess }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const decagonChatPath = getDecagonChatPath(pathname, search);

  const isMobile = useSelector(selectIsMobile);
  const locale = useSelector(selectLocale);
  const venue = useSelector(selectActiveVenue);
  const modals = useSelector(selectModals);
  const isAvailabilityModalOpenRef = useRef(false);

  isAvailabilityModalOpenRef.current = !!modals[ITEM_AVAILABILITY_MODAL_NAME]?.isOpen;

  const { currencyCode } = venue;

  const { currentItem, setCurrentItem } = useContext(ItemFormContext);
  const { closeFlyout } = useContext(FlyoutContext);
  const formRef = useRef(null);
  const [showAvailabilityRecurrence, setShowAvailabilityRecurrence] = useState(false);
  const [shouldUpdateAllItemAvailabilities, setShouldUpdateAllItemAvailabilities] = useState(false);
  const [editItemFlyoutId, setEditItemFlyoutId] = useState(null);

  const {
    activeTagIds = [],
    availabilityRecurrence = [],
    availabilityStatus = '',
    name = '',
    description = '',
    category = '',
    payoutRate = null,
  } = currentItem;
  const isPending = payoutRate === null;

  let currentCategoryTag = '';
  if (category) {
    currentCategoryTag = categoryTags.find(tag => tag.name === category) || '';
  } else if (activeTagIds.includes(foodAndBeverageL1Tag?.id)) {
    currentCategoryTag = foodAndBeverageL1Tag;
  }

  useEffect(() => {
    const flyoutId = generateFlyoutId(EDIT_ITEM_FLYOUT_ID_PREFIX, name);
    setEditItemFlyoutId(flyoutId);
  }, [currentItem]);

  const handleClose = () => {
    closeFlyout();
    setShowAvailabilityRecurrence(false);
    setCurrentItem({});
  };

  const toggleShowAvailabilityRecurrence = () => {
    setShowAvailabilityRecurrence(!showAvailabilityRecurrence);
  };

  const getDefaultValues = () => {
    return {
      name,
      description,
      category: currentCategoryTag,
      availabilityStatus,
      availabilityRecurrence,
    };
  };

  function overrideFlyoutOutsideClick(event) {
    if (isAvailabilityModalOpenRef.current) return true;
    // orphaned target = click originated within the modal that was removed during propagation.
    if (event?.target && !document.body.contains(event.target)) return true;
    return false;
  }

  return (
    <Flyout
      id={editItemFlyoutId}
      className={block()}
      onClose={handleClose}
      showBackButton={isMobile}
      overrideOutsideClick={overrideFlyoutOutsideClick}>
      <FlyoutTitle>
        <Message className={element('title')} message={messages.editItem} values={{ itemName: name }} />
      </FlyoutTitle>
      <Form isLoading={isLoading} id={FORM_NAME} defaultValues={getDefaultValues()}>
        {function FormRender({ formState, handleSubmit, reset, setValue, watch }) {
          const { errors, isDirty, isSubmitted, isSubmitting, submitCount } = formState;
          const [watchAvailabilityRecurrence, watchItemName = '', watchItemDescription = ''] = watch([
            'availabilityRecurrence',
            'name',
            'description',
          ]);

          const resetForm = () => {
            if (formRef.current) {
              formRef.current.scrollIntoView();
            }
            setShowAvailabilityRecurrence(false);

            const defaultValues = getDefaultValues();
            reset(defaultValues);
          };

          useEffect(() => {
            resetForm();
          }, [currentItem]);

          useEffect(() => {
            if (isSubmitted && !_isEmpty(errors)) {
              if (formRef.current) {
                formRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }, [submitCount]);

          const onSubmit = async values => {
            const currentValues = {
              ...currentItem,
              currentCategoryTag,
            };

            try {
              await updateItem(
                items,
                venue,
                foodAndBeverageL1Tag,
                shouldUpdateAllItemAvailabilities,
                currentValues,
                values
              );
              onSuccess?.();
              handleClose();
              dispatch(
                createSuccessSnackbar(
                  messages.successEditItem,
                  {
                    itemName: name,
                    strong: chunks => <strong>{chunks}</strong>,
                  },
                  { isLowStyle: true }
                )
              );
            } catch (error) {
              handleClose();
              reportError('Error updating item', error);
              dispatch(
                createErrorSnackbar(
                  messages.errorEditItem,
                  { strong: chunks => <strong>{chunks}</strong> },
                  { isLowStyle: true },
                  getApiErrorSnackbarDescription(error, chunks => (
                    <Link className={element('snackbar-link')} to={decagonChatPath}>
                      {chunks}
                    </Link>
                  ))
                )
              );
            }
          };

          async function openItemAvailabilityModal() {
            const itemAvailabilityPromise = new Promise(resolve => {
              const initialAvailability = watchAvailabilityRecurrence || [];

              function onSubmitItemAvailability({ availabilityRecurrence: itemAvailabilityRecurrence, updateAll }) {
                resolve({ updatedAvailability: itemAvailabilityRecurrence, updateAll });
              }

              dispatch(
                openModal(ITEM_AVAILABILITY_MODAL_NAME, {
                  isEditForm: true,
                  initialAvailability,
                  onSubmit: onSubmitItemAvailability,
                  id: MODAL_ID,
                })
              );
            });

            const { updateAll, updatedAvailability } = await itemAvailabilityPromise;
            if (updatedAvailability) {
              setValue(
                'availabilityRecurrence',
                updateAvailabilityRecurrence(availabilityRecurrence, updatedAvailability),
                { shouldDirty: true }
              );
              setShouldUpdateAllItemAvailabilities(updateAll);
            }
            dispatch(closeModal(ITEM_AVAILABILITY_MODAL_NAME));
          }

          return (
            <>
              <FlyoutContent>
                <div className={element('input-wrapper')} ref={formRef}>
                  <When predicate={!_isEmpty(errors)}>
                    <div className={element('error-banner')}>
                      <Isvg className={element('error-icon')} src={ICONS.error} />
                      <Message as="div" message={messages.submitError} />
                    </div>
                  </When>
                  <div className={element('availability-status-wrapper')}>
                    <ItemAvailabilityStatusDropdown name="availabilityStatus" isInEditForm isPending={isPending} />
                  </div>
                  <Input
                    name="name"
                    disabled={isSubmitting}
                    label={messages.itemNameLabel}
                    maxLength={NAME_MAX_INPUT_LENGTH}
                    placeholder={messages.itemNamePlaceholder}
                    type="text"
                    validate={{
                      message: messages.itemNameMustBeUnique,
                      value: value => (value !== name ? !existingItemNames.includes(value.toLowerCase()) : true),
                    }}
                    required
                    showRequiredLabel
                  />
                  <When predicate={NAME_MAX_INPUT_LENGTH - watchItemName.length <= 10}>
                    <div
                      className={element('characters-remaining', {
                        error: NAME_MAX_INPUT_LENGTH === watchItemName.length,
                      })}>
                      <CharactersRemaining length={NAME_MAX_INPUT_LENGTH} value={watchItemName} showMaxLength />
                    </div>
                  </When>
                  <Textarea
                    name="description"
                    disabled={isSubmitting}
                    heightInLines={DESCRIPTION_INPUT_HEIGHT_IN_LINES}
                    label={messages.itemDescriptionLabel}
                    maxLength={DESCRIPTION_MAX_INPUT_LENGTH}
                    placeholder={messages.itemDescriptionPlaceholder}
                    type="text"
                    required
                    showRequiredLabel
                  />
                  <When predicate={DESCRIPTION_MAX_INPUT_LENGTH - watchItemDescription.length <= 10}>
                    <div
                      className={element('characters-remaining', {
                        error: DESCRIPTION_MAX_INPUT_LENGTH === watchItemDescription.length,
                      })}>
                      <CharactersRemaining
                        length={DESCRIPTION_MAX_INPUT_LENGTH}
                        value={watchItemDescription}
                        showMaxLength
                      />
                    </div>
                  </When>
                  <Dropdown
                    className={element('category-dropdown')}
                    name="category"
                    disabled={isSubmitting}
                    label={messages.selectCategory}
                    required
                    showRequiredLabel>
                    {({ value: selectedOption, onChange }) => {
                      return (
                        <>
                          <DropdownToggle className={element('category-dropdown-toggle', { selected: selectedOption })}>
                            <When predicate={!selectedOption}>
                              <Message as="div" message={messages.selectCategory} />
                            </When>
                            <When predicate={!!selectedOption}>
                              {selectedOption.id === foodAndBeverageL1Tag?.id ? (
                                <Message as="div" message={messages.otherCategory} />
                              ) : (
                                selectedOption.name || selectedOption
                              )}
                            </When>
                          </DropdownToggle>
                          <DropdownMenu>
                            {categoryTags.map(categoryTag => {
                              return (
                                <DropdownItem
                                  key={categoryTag.id}
                                  isSelected={selectedOption?.id === categoryTag.id}
                                  onClick={() => onChange(categoryTag)}>
                                  <div>{categoryTag.name}</div>
                                </DropdownItem>
                              );
                            })}
                            <DropdownItem
                              key={foodAndBeverageL1Tag?.id}
                              isSelected={selectedOption?.id === foodAndBeverageL1Tag?.id}
                              onClick={() => onChange(foodAndBeverageL1Tag)}>
                              <Message as="div" message={messages.otherCategory} />
                            </DropdownItem>
                          </DropdownMenu>
                        </>
                      );
                    }}
                  </Dropdown>
                  <div className={element('payout-rate')}>
                    <span className={element('payout-label-wrapper')}>
                      <Message as="div" className={element('payout-label')} message={messages.payoutLabel} />
                      <TooltipToggle className={element('tooltip-toggle')} name="edit-form-payout-tooltip">
                        <Isvg src={ICONS.infoGreyLight} />
                      </TooltipToggle>
                      <TooltipContent hoverable name="edit-form-payout-tooltip" place="right">
                        <Message
                          message={messages.payoutTooltip}
                          values={{
                            a: text => (
                              <a href={makePath.partnerTaxDefinition(locale)} target="_blank" rel="noopener noreferrer">
                                {text}
                              </a>
                            ),
                          }}
                        />
                      </TooltipContent>
                    </span>
                    {payoutRate ? (
                      <Message
                        as="div"
                        className={element('payout-text')}
                        format="currency"
                        currency={currencyCode}
                        value={payoutRate}
                      />
                    ) : (
                      <div className={element('payout-text')}>––</div>
                    )}
                  </div>
                  <div className={element('info-notice')}>
                    <div className={element('info-content')}>
                      <Isvg className="info-icon" src={ICONS.infoBrand} />
                      <Message className="info-message" message={messages.editPriceBanner} />
                    </div>
                    <Link className={element('contact-link')} to={decagonChatPath}>
                      <Message as={Button} className={element('contact-btn')} message={messages.contactButton} />
                    </Link>
                  </div>

                  <div className={element('item-availability-dropdown')}>
                    <button
                      type="button"
                      className={element('item-availability-expand-btn')}
                      onClick={toggleShowAvailabilityRecurrence}
                      aria-expanded={showAvailabilityRecurrence}
                      aria-label={
                        showAvailabilityRecurrence
                          ? 'Collapse availability recurrence'
                          : 'Expand availability recurrence'
                      }>
                      <div
                        className={element('item-availability-title-wrapper', {
                          isExpanded: showAvailabilityRecurrence,
                        })}>
                        <Message
                          as="div"
                          className={element('item-availability-label')}
                          message={messages.itemAvailabilityLabel}
                        />
                        <Message as="div" message={messages.optionalLabel} />
                        <SvgArrow direction={showAvailabilityRecurrence ? 'up' : 'down'} />
                      </div>
                    </button>
                    <When predicate={showAvailabilityRecurrence}>
                      <div className={element('item-availability-description-wrapper')}>
                        <Message
                          as="div"
                          className={element('item-availability-description')}
                          message={messages.itemAvailabilityDescription}
                        />
                        <Button
                          className={element('icon-button')}
                          styled="tertiary"
                          onClick={openItemAvailabilityModal}>
                          <Isvg src={ICONS.pencilFilled} />
                          <Message className={element('button-text')} as="div" message={messages.editButton} />
                        </Button>
                      </div>
                      {watchAvailabilityRecurrence?.map(day => {
                        const { available, times, weekday } = day;
                        const sortedTimes = times.sort((a, b) => {
                          const startTimeA = parse(a.startTime, 'HH:mm', new Date());
                          const startTimeB = parse(b.startTime, 'HH:mm', new Date());
                          return startTimeA - startTimeB;
                        });

                        return (
                          <div key={weekday} className={element('item-availability-day')}>
                            <div>{weekday}</div>
                            <When predicate={!available}>
                              <Message message={messages.availabilityUnavailable} />
                            </When>
                            <When predicate={available}>
                              <div className={element('item-availability-times')}>
                                {sortedTimes.map(({ startTime, endTime }) => {
                                  const parsedStartTime = parse(startTime, 'HH:mm', new Date());
                                  const parsedEndTime = parse(endTime, 'HH:mm', new Date());

                                  const formattedStartTime = format12HourTimeLowercase(parsedStartTime, locale);
                                  const formattedEndTime = format12HourTimeLowercase(parsedEndTime, locale);

                                  return (
                                    <Message
                                      key={`${weekday}-${startTime}`}
                                      message={messages.availabilityTimeWindow}
                                      values={{ startTime: formattedStartTime, endTime: formattedEndTime }}
                                    />
                                  );
                                })}
                              </div>
                            </When>
                          </div>
                        );
                      })}
                    </When>
                  </div>
                </div>
              </FlyoutContent>
              <FlyoutFooter>
                <ButtonGroup className={element('button-group')} divider={false} flush>
                  <Message
                    as={Button}
                    className={element('cancel-btn')}
                    disabled={isSubmitting}
                    message={messages.cancelButton}
                    onClick={handleClose}
                    styled="tertiary"
                    type={Button.TYPES.BUTTON}
                  />
                  <Message
                    form={FORM_NAME}
                    as={Button}
                    className={element('submit-btn')}
                    disabled={!isDirty || isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    message={messages.saveButton}
                    styled="primary"
                    type={Button.TYPES.SUBMIT}
                  />
                </ButtonGroup>
              </FlyoutFooter>
            </>
          );
        }}
      </Form>
    </Flyout>
  );
};

EditItemForm.propTypes = {
  existingItemNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool,
  categoryTags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  foodAndBeverageL1Tag: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  items: PropTypes.arrayOf(itemPropType),
  onSuccess: PropTypes.func,
};

export default EditItemForm;
